import React, { useState, useEffect } from 'react';
import { formatTime12h, getCookie } from '../utils';

export default function AttendanceModal({ enrollmentId, enrollments, students, closeModal }) {
  const [attendanceRecords, setAttendanceRecords] = useState([]);
  const [isLoading, setIsLoading] = useState(true);

  // Safely find associated local data
  const enroll = enrollments.find(e => e.id === enrollmentId);
  const student = students.find(s => s.id === enroll?.studentId);

  useEffect(() => {
    const fetchAttendance = async () => {
      try {
        const token = getCookie('tsa_token');
        const res = await fetch('https://tsaapis.operate.live/api/attendance', {
          method: 'GET',
          headers: {
            'Content-Type': 'application/json',
            'x-auth-token': token || ''
          },
          credentials: 'include'
        });

        if (res.ok) {
          const data = await res.json();
          
          // Filter records matching this specific enrollment
          // Handles both string IDs and populated Mongoose object IDs
          const enrollmentAttendance = data.filter(a => 
            a.EnrolmentId === enrollmentId || 
            (a.EnrolmentId && a.EnrolmentId._id === enrollmentId)
          );
          
          // Sort by newest login first
          enrollmentAttendance.sort((a, b) => new Date(b.LoginDT) - new Date(a.LoginDT));
          
          setAttendanceRecords(enrollmentAttendance);
        }
      } catch (err) {
        console.error("API Error fetching attendance:", err);
      } finally {
        setIsLoading(false);
      }
    };

    if (enrollmentId) {
      fetchAttendance();
    }
  }, [enrollmentId]);

  // Helper function to calculate duration between LoginDT and LogoutDT
  const calculateTotalTime = (start, end) => {
    if (!end) return 'Active Session';
    
    const diffMs = new Date(end).getTime() - new Date(start).getTime();
    const totalHours = diffMs / 3600000; // Convert ms to hours
    
    // If it's less than 0.01 hours, show 0, otherwise show up to 2 decimals
    return `${Math.max(0, totalHours).toFixed(2)} Hrs`;
  };

  // Helper to safely format time handling undefined values
  const safelyFormatTime = (isoString) => {
    if (!isoString) return 'Ongoing';
    // Fallback to JS standard time string if formatTime12h expects a specific format
    try {
      return new Date(isoString).toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' });
    } catch {
      return formatTime12h(isoString);
    }
  };

  return (
    <div className="modal-overlay">
      <div className="modal-content">
        <button className="close-modal" onClick={closeModal}>&times;</button>
        <h2>{student?.name || 'Student'}'s Attendance</h2>
        
        {isLoading ? (
          <div style={{ textAlign: 'center', padding: '30px' }}>
             <i className="fas fa-spinner fa-spin fa-2x" style={{ color: 'var(--accent-gold)', marginBottom: '10px' }}></i>
             <p style={{ color: 'var(--text-gray)' }}>Loading attendance history...</p>
          </div>
        ) : (
          <div className="table-responsive">
            <table>
              <thead>
                <tr>
                  <th>Date</th>
                  <th>Start</th>
                  <th>End</th>
                  <th>Total Time</th>
                </tr>
              </thead>
              <tbody>
                {attendanceRecords.length === 0 ? (
                  <tr>
                    <td colSpan="4" style={{ textAlign: 'center', padding: '20px', color: 'var(--text-gray)' }}>
                      No attendance records found.
                    </td>
                  </tr>
                ) : (
                  attendanceRecords.map((record) => (
                    <tr key={record._id || record.id}>
                      <td>{new Date(record.LoginDT).toLocaleDateString()}</td>
                      <td>{safelyFormatTime(record.LoginDT)}</td>
                      <td>
                        {record.LogoutDT ? (
                          safelyFormatTime(record.LogoutDT)
                        ) : (
                          <span style={{ color: '#10b981', fontWeight: 'bold' }}>Active</span>
                        )}
                      </td>
                      <td style={{ 
                        color: record.LogoutDT ? 'var(--accent-gold)' : '#10b981', 
                        fontWeight: record.LogoutDT ? 'normal' : 'bold' 
                      }}>
                        {calculateTotalTime(record.LoginDT, record.LogoutDT)}
                      </td>
                    </tr>
                  ))
                )}
              </tbody>
            </table>
          </div>
        )}
      </div>
    </div>
  );
}